import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs/Observable'
import 'rxjs/add/operator/map'
import 'rxjs/add/operator/switchMap'
import 'rxjs/add/operator/do'
import { Router } from '@angular/router'
import { environment } from '../environments/environment'
import { TokenModel } from './main/authentication/models/token.model'
import { BehaviorSubject } from 'rxjs/BehaviorSubject'
import { DeviceModel } from './main/authentication/models/device.model'

// Avoid name not found warnings
declare var auth0: any

@Injectable()
export class AuthService {
  onSuccesLogin: BehaviorSubject<any> = new BehaviorSubject(false)
  loggedIn: boolean
  currentRefreshToken: string

  // Create Auth0 web auth instance
  private readonly auth0 = {
    clientID: environment.oauth_token,
    secret: environment.oauth_secret,
    responseType: 'token id_token',
    url: environment.base_url,
    grant: environment.grant,
    grant_2fa: environment.grant_2fa,
  }
  private readonly loginUrl = `${this.auth0.url}/oauth/v2/token`
  private readonly magicTokenUrl = `${this.auth0.url}/v2/login/magic-token`
  private readonly validateMagicTokenUrl = `${this.auth0.url}/v2/login/token-validation`

  static getToken(): TokenModel {
    let token: TokenModel

    try {
      token = new TokenModel(JSON.parse(localStorage.getItem('token')))
    } catch (err) {
      console.log(err)
    }
    return token
  }

  static saveTokenInLocalStorage(token: TokenModel) {
    localStorage.setItem('token', JSON.stringify(token))
  }

  static removeTokenFromLocalStorage(key: string = 'token') {
    localStorage.removeItem('token')
  }

  constructor(private router: Router, private httpClient: HttpClient) {
    this.loggedIn = false
  }

  onLoggedIn(token: TokenModel) {
    this.loggedIn = true
    this.onSuccesLogin.next(true)
  }

  /**
   * Fetches a refreshToken from the backend
   */
  refreshToken() {
    return this.httpClient
      .post<TokenModel>(this.loginUrl, {
        grant_type: 'refresh_token',
        refresh_token: this.getRefreshToken(),
        client_id: this.auth0.clientID,
        client_secret: this.auth0.secret,
      })
      .do(AuthService.saveTokenInLocalStorage)
  }

  /**
   * Returns the refresh token which is stored in the localStorage
   */
  getRefreshToken(): string {
    const token: TokenModel = AuthService.getToken()
    this.currentRefreshToken = token.refresh_token
    return token.refresh_token
  }

  /**
   *
   * @param {DeviceModel} device
   */
  public twoFactor(device: DeviceModel): Observable<TokenModel> {
    const token: TokenModel = AuthService.getToken()

    return this.httpClient
      .post<TokenModel>(this.loginUrl, {
        grant_type: this.auth0.grant_2fa,
        device_id: device.device_id,
        token: token.access_token,
        client_id: this.auth0.clientID,
        client_secret: this.auth0.secret,
      })
      .do(AuthService.saveTokenInLocalStorage)
  }

  goToLock() {
    AuthService.removeTokenFromLocalStorage()
    // Return to homepage
    this.router.navigate(['/lock'])
  }

  logout() {
    Observable.throw('')
    localStorage.clear()
    // Return to homepage
    this.router.navigate(['/login'])
  }

  /**
   * Sends an email with a magic link
   * @param email
   */
  getMagicToken(email: string) {
    return this.httpClient.post<{}>(this.magicTokenUrl, {
      email: email,
      origin: 'support',
    })
  }

  /**
   * Verify magic token and proceed to oauth flow
   * @param token
   */
  verifyMagicLinkAndRefresh(token: string): Observable<TokenModel> {
    const body = {
      token: token,
      client_id: this.auth0.clientID,
      client_secret: this.auth0.secret,
    }

    return this.httpClient
      .post<{ token: string }>(this.validateMagicTokenUrl, body)
      .flatMap((value: { token: string }) => {
        return this.getOauthWithRefreshToken(value.token)
      })
      .catch((error: any) => {
        return Observable.throw(error)
      })
  }

  /**
   * Returns oauth tokens and stores them in local storage
   * @param refreshToken
   */
  private getOauthWithRefreshToken(refreshToken: string): Observable<TokenModel> {
    const body = {
      refresh_token: refreshToken,
      grant_type: 'refresh_token',
      client_id: this.auth0.clientID,
      client_secret: this.auth0.secret,
    }

    return this.httpClient
      .post<TokenModel>(this.loginUrl, body)
      .do(AuthService.saveTokenInLocalStorage)
      .map(token => token)
      .catch((error: any) => {
        return Observable.throw(error)
      })
  }
}



// WEBPACK FOOTER //
// ./src/app/auth.service.ts